import { useEffect, useRef, useState } from 'react';

import type { ExtractedInvoice, RdVerifyResult, SupplierBranch, Vendor } from '@dcc-erp/shared';

import { useAddBranch, useCreateSupplier, useSupplierByTaxId } from '../../lib/queries/suppliers';
import { AppModal } from '../AppModal';
import { SearchSelect } from '../SearchSelect';
import { RdVerifyChip } from './RdVerifyChip';

// `normalizeBranch` lives only in the API (apps/api/src/modules/doc-ingest/
// tax-id.ts) — it is NOT exported from @dcc-erp/shared or the web bundle. Per the
// C2.5 reconcile note we keep a thin web-local copy with the same semantics:
// Thai digits → Arabic, head-office wording → 00000, else the first run of
// digits padded/sliced to 5.
function normalizeBranch(raw: string | null | undefined): string {
  const s = (raw ?? '').replace(/[๐-๙]/g, (d) => String('๐๑๒๓๔๕๖๗๘๙'.indexOf(d))).trim();
  if (/สำนักงานใหญ่|head\s*office/i.test(s)) return '00000';
  const m = /\d+/.exec(s);
  if (!m) return '';
  return m[0].padStart(5, '0').slice(-5);
}

export interface VendorMatchCardProps {
  companyId: string | undefined;
  extracted: ExtractedInvoice;
  /** Selected branch vendorId (the transactable unit) controlled by the route. */
  value: string | null;
  onChange: (vendorId: string | null) => void;
  /** {value:id, label:'name (taxId)'} options for the manual picker. */
  vendorOptions: { value: string; label: string }[];
  /** RD verdict from the proposal (Plan C1). null = DISABLED / no verdict. */
  rdVerify?: RdVerifyResult | null;
  /** Re-run RD verify (UNKNOWN). */
  onRecheck?: () => void;
  recheckBusy?: boolean;
  onQuickAdded?: (v: Vendor) => void;
}

export function VendorMatchCard({
  companyId,
  extracted,
  value,
  onChange,
  vendorOptions,
  rdVerify,
  onRecheck,
  recheckBusy,
  onQuickAdded,
}: VendorMatchCardProps) {
  const sellerTaxId = extracted.seller.taxId.value;
  const invoiceBranch = normalizeBranch(extracted.seller.branch.value ?? '00000');
  const supplierQ = useSupplierByTaxId(companyId, sellerTaxId);
  const supplier = supplierQ.data ?? null;
  // The by-tax-id endpoint returns the Supplier entity with its branch rows under
  // `.vendors` (a Vendor IS the transactable branch).
  const branches = supplier?.vendors ?? [];
  const matchedBranch = branches.find((b) => normalizeBranch(b.branchCode) === invoiceBranch);

  const [addSupplier, setAddSupplier] = useState(false);
  const [addBranch, setAddBranch] = useState(false);
  const [cleared, setCleared] = useState(false);

  // Auto-select the matching branch once, when the supplier query resolves.
  const prefilled = useRef(false);
  useEffect(() => {
    if (!prefilled.current && matchedBranch && !value) {
      prefilled.current = true;
      onChange(matchedBranch.id);
    }
  }, [matchedBranch, value, onChange]);

  const labelFor = (id: string) => vendorOptions.find((o) => o.value === id)?.label ?? id;
  const showMatched = !!matchedBranch && !cleared && (value === null || value === matchedBranch.id);

  return (
    <div
      className="card"
      style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 8 }}
    >
      <RdVerifyChip
        result={rdVerify ?? null}
        {...(onRecheck ? { onRecheck } : {})}
        {...(recheckBusy !== undefined ? { recheckBusy } : {})}
      />

      {showMatched && matchedBranch ? (
        <div style={{ marginTop: 8 }}>
          <div className="badge positive">✓ จับคู่ผู้ขาย/สาขาจากเลขภาษี</div>
          <div style={{ marginTop: 6, fontWeight: 600 }}>{supplier?.nameTh}</div>
          <div className="meta">
            เลขภาษี {supplier?.taxId} · สาขา {matchedBranch.branchCode}
            {matchedBranch.branchName ? ` (${matchedBranch.branchName})` : ''}
          </div>
          {/* Reconcile (a): master diverges from RD → อัปเดตจาก RD */}
          {rdVerify?.found &&
            rdVerify.registeredName &&
            supplier?.nameTh !== rdVerify.registeredName && (
              <div className="meta" style={{ color: 'var(--warning)', marginTop: 4 }}>
                ทะเบียน RD: {rdVerify.registeredName} (ต่างจากต้นแบบ)
              </div>
            )}
          <button
            type="button"
            className="btn sm ghost"
            style={{ marginTop: 6 }}
            onClick={() => {
              prefilled.current = true;
              setCleared(true);
              onChange(null);
            }}
          >
            เปลี่ยนผู้ขาย/สาขา
          </button>
        </div>
      ) : (
        <div style={{ marginTop: 8 }}>
          <label className="meta" style={{ display: 'block', marginBottom: 4 }}>
            ผู้ขาย (สาขา) *
          </label>
          {/* Branch picker: when the supplier exists, prefer its branches. */}
          <SearchSelect
            value={value ?? ''}
            onChange={(id) => onChange(id || null)}
            options={
              branches.length
                ? branches.map((b) => ({
                    value: b.id,
                    label: `${supplier?.nameTh} — สาขา ${b.branchCode}${b.branchName ? ` (${b.branchName})` : ''}`,
                  }))
                : vendorOptions
            }
            getLabel={labelFor}
            placeholder="ค้นหาผู้ขาย/สาขา (ชื่อ / เลขภาษี)"
          />
          <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
            {/* (b) supplier exists, branch missing, RD found → เพิ่มสาขาจาก RD */}
            {supplier && !matchedBranch && rdVerify?.found && (
              <button type="button" className="btn sm ghost" onClick={() => setAddBranch(true)}>
                ＋ เพิ่มสาขาจาก RD
              </button>
            )}
            {/* (c) no supplier + RD found → เพิ่มผู้ขายจาก RD */}
            {!supplier && rdVerify?.found && (
              <button type="button" className="btn sm ghost" onClick={() => setAddSupplier(true)}>
                ＋ เพิ่มผู้ขายจาก RD
              </button>
            )}
            {/* (d) RD not found/unknown → manual add (OCR-prefilled), bill carries anomaly */}
            {!supplier && !rdVerify?.found && (
              <button type="button" className="btn sm ghost" onClick={() => setAddSupplier(true)}>
                ＋ เพิ่มผู้ขายใหม่
              </button>
            )}
          </div>
        </div>
      )}

      {addSupplier && (
        <SupplierFromRdModal
          companyId={companyId}
          extracted={extracted}
          rdVerify={rdVerify ?? null}
          invoiceBranch={invoiceBranch}
          onClose={() => setAddSupplier(false)}
          onAdded={(branchId, v) => {
            setAddSupplier(false);
            onChange(branchId);
            if (v) onQuickAdded?.(v);
          }}
        />
      )}

      {addBranch && supplier && (
        <BranchFromRdModal
          companyId={companyId}
          supplierId={supplier.id}
          supplierName={supplier.nameTh}
          supplierTaxId={supplier.taxId ?? null}
          rdVerify={rdVerify ?? null}
          invoiceBranch={invoiceBranch}
          onClose={() => setAddBranch(false)}
          onAdded={(branchId) => {
            setAddBranch(false);
            onChange(branchId);
          }}
        />
      )}
    </div>
  );
}

// Add a Supplier (entity + first branch), RD-prefilled, edit-before-save.
function SupplierFromRdModal({
  companyId,
  extracted,
  rdVerify,
  invoiceBranch,
  onClose,
  onAdded,
}: {
  companyId: string | undefined;
  extracted: ExtractedInvoice;
  rdVerify: RdVerifyResult | null;
  invoiceBranch: string;
  onClose: () => void;
  onAdded: (branchId: string, v?: Vendor) => void;
}) {
  const create = useCreateSupplier(companyId);
  const rdName = rdVerify?.registeredName ?? '';
  const ocrName = extracted.seller.name.value ?? '';
  const [nameTh, setNameTh] = useState(rdName || ocrName);
  const [taxId, setTaxId] = useState(extracted.seller.taxId.value ?? '');
  const [branchCode, setBranchCode] = useState(rdVerify?.branchNumber ?? invoiceBranch);
  const [branchName, setBranchName] = useState(rdVerify?.branchName ?? '');
  const [addressTh, setAddressTh] = useState(
    rdVerify?.addressTh ?? extracted.seller.address.value ?? '',
  );

  async function save() {
    // useCreateSupplier returns the `{ supplier, branch }` envelope (real backend
    // contract). `branch` is the freshly-created Vendor row (transactable unit).
    const res = await create.mutateAsync({
      code: taxId.slice(-6),
      nameTh,
      taxId,
      country: 'TH',
      currency: 'THB',
      isActive: true,
      branch: {
        nameTh,
        taxId,
        branchCode,
        branchName: branchName || null,
        addressTh: addressTh || null,
        country: 'TH',
        currency: 'THB',
        isActive: true,
      },
    });
    onAdded(res.branch.id, res.branch as unknown as Vendor);
  }

  return (
    <AppModal
      title={rdVerify?.found ? 'เพิ่มผู้ขายจาก RD' : 'เพิ่มผู้ขายใหม่'}
      subtitle={
        rdVerify?.found
          ? 'ข้อมูลจากทะเบียนกรมสรรพากร — แก้ไขก่อนบันทึก'
          : 'กรอกจากข้อมูลที่อ่านได้จากเอกสาร'
      }
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={create.isPending}
            onClick={() => void save()}
          >
            {create.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 10 }}>
        <label className="meta">ชื่อผู้ขาย (ทะเบียน)</label>
        <input className="input" value={nameTh} onChange={(e) => setNameTh(e.target.value)} />
        <label className="meta">เลขประจำตัวผู้เสียภาษี</label>
        <input className="input" value={taxId} onChange={(e) => setTaxId(e.target.value)} />
        <label className="meta">รหัสสาขา</label>
        <input
          className="input"
          value={branchCode}
          onChange={(e) => setBranchCode(e.target.value)}
        />
        <label className="meta">ชื่อสาขา</label>
        <input
          className="input"
          value={branchName}
          onChange={(e) => setBranchName(e.target.value)}
        />
        <label className="meta">ที่อยู่</label>
        <input className="input" value={addressTh} onChange={(e) => setAddressTh(e.target.value)} />
      </div>
    </AppModal>
  );
}

// Add a branch to an existing Supplier, RD-prefilled, edit-before-save.
function BranchFromRdModal({
  companyId,
  supplierId,
  supplierName,
  supplierTaxId,
  rdVerify,
  invoiceBranch,
  onClose,
  onAdded,
}: {
  companyId: string | undefined;
  supplierId: string;
  supplierName: string;
  supplierTaxId: string | null;
  rdVerify: RdVerifyResult | null;
  invoiceBranch: string;
  onClose: () => void;
  onAdded: (branchId: string) => void;
}) {
  const addBranch = useAddBranch(companyId);
  const [branchCode, setBranchCode] = useState(rdVerify?.branchNumber ?? invoiceBranch);
  const [branchName, setBranchName] = useState(rdVerify?.branchName ?? '');
  const [addressTh, setAddressTh] = useState(rdVerify?.addressTh ?? '');

  async function save() {
    // useAddBranch takes `{ id, data }` and returns the created branch row
    // (typed `unknown` at the hook boundary — narrow to read its id). The branch
    // inherits the parent supplier's name + taxId (a Vendor row carries both).
    const v = (await addBranch.mutateAsync({
      id: supplierId,
      data: {
        nameTh: supplierName,
        taxId: supplierTaxId,
        branchCode,
        branchName: branchName || null,
        addressTh: addressTh || null,
        country: 'TH',
        currency: 'THB',
        isActive: true,
      },
    })) as SupplierBranch;
    onAdded(v.id);
  }

  return (
    <AppModal
      title="เพิ่มสาขาจาก RD"
      subtitle="ข้อมูลสาขาจากทะเบียนกรมสรรพากร — แก้ไขก่อนบันทึก"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={addBranch.isPending}
            onClick={() => void save()}
          >
            {addBranch.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 10 }}>
        <label className="meta">รหัสสาขา</label>
        <input
          className="input"
          value={branchCode}
          onChange={(e) => setBranchCode(e.target.value)}
        />
        <label className="meta">ชื่อสาขา</label>
        <input
          className="input"
          value={branchName}
          onChange={(e) => setBranchName(e.target.value)}
        />
        <label className="meta">ที่อยู่</label>
        <input className="input" value={addressTh} onChange={(e) => setAddressTh(e.target.value)} />
      </div>
    </AppModal>
  );
}
